import { DEFAULT_DECIMAL_PLACES } from '@shared/models/decimal-places';

export class GlobalSettings {
  modulesActivation: ModulesActivation = new ModulesActivation();
  preventMultipleTabs: boolean = false;
  amountsDecimalPlaces: number = DEFAULT_DECIMAL_PLACES.amount;
  quantityDecimalPlaces: number = DEFAULT_DECIMAL_PLACES.quantity;
  desktopApp: DesktopAppSettings = new DesktopAppSettings();

  static clone(value: any) {
    const settings = Object.assign(new GlobalSettings(), value);
    settings.desktopApp = Object.assign(new DesktopAppSettings(), value?.desktopApp);
    return settings;
  }
}

export class DesktopAppSettings {
  dataReplication: boolean = false;

  /** How many tasks are enabled — drives the "Not in use" / "Enabled · N tasks" header status */
  static countEnabledTasks(tasks: Partial<DesktopAppSettings> | null | undefined): number {
    return Object.values(tasks ?? {}).filter(enabled => enabled === true).length;
  }
}

export class ModulesActivation {
  purchasing: boolean = false;
  pointOfSale: boolean = false;
  invoicing: boolean = false;
  manufacturing: boolean = false;
  fixedAssets: boolean = false;
  analytics: boolean = false;
  moduleTransactions: ModuleTransactions = new ModuleTransactions();
  moduleVersionConfig: ModuleVersionConfig = new ModuleVersionConfig();
}

export class ModuleTransactions {
  invoicingTransactions?: InvoicingTransactions;
}

export class ModuleVersionConfig {
  posVersion: ModuleVersionSettings = new ModuleVersionSettings();
}

export class ModuleVersionSettings {
  version: ModuleVersion = ModuleVersion.V3_0;
  usersLevelVersion?: UserVersionConfig[];
}

export class UserVersionConfig {
  user!: string;
  version: ModuleVersion.V3_0 | ModuleVersion.V2_5 = ModuleVersion.V3_0;
}

export class OrganizationProfile {
  organizationName!: string;
  country!: number;
  taxRegistrationNumber?: string;
  commercialRegistrationNumber?: string;
  currency!: number;
  industry?: string;
  address?: any;
  businessDomain?: BusinessDomain;

  static clone(value: any) {
    const settings = new OrganizationProfile();
    Object.assign(settings, value);
    return settings;
  }
}

export class BusinessDomain {
  sectors: string[] = [];
  productLines: string[] = [];
  salesChannels: string[] = [];
  businessEngagement: string[] = [];
}

export class OrdersSetting {
  useSalesTax: boolean = true;
  fulfillmentMode: FulfillmentMode = FulfillmentMode.Automatic;
  automaticFulfillmentCondition: AutomaticFulfillmentCondition = AutomaticFulfillmentCondition.WhenOrderPaidAndDelivered;
  salesRecognition: SalesRecognitionSetting = SalesRecognitionSetting.Fulfilled;
  availableToSellFormula: AvailableToSellFormula = AvailableToSellFormula.OnHand_Refund_Orders_PreOrders
  paymentTerminal: boolean = false;

  static clone(value: any) {
    const settings = new OrdersSetting();
    Object.assign(settings, value);
    return settings;
  }
}

export class PurchaseInvoicesSetting {
  compoundDiscount: boolean = false;

  static clone(value: any) {
    const settings = new PurchaseInvoicesSetting();
    Object.assign(settings, value);
    return settings;
  }
}

export class SalesInvoicesSetting {
  defaultTaxId!: string;
  productPrices: InvoicingProductPrices = InvoicingProductPrices.Inclusive;
  einvoice_ksa_configuration?: EInvoiceKsaConfiguration;
  submissionMode: SubmissionMode = SubmissionMode.Later;

  static clone(value: any) {
    const settings = new SalesInvoicesSetting();
    Object.assign(settings, value);
    return settings;
  }
}

export interface EInvoiceKsaConfiguration {
  firstConnectionDate: Date
  connected: boolean;
  environment: string;
  invoiceType: string;
  otp: string;
  commonName: string;
  taxRegistrationNumber: string;
  branchName: string;
  organizationName: string;
  countryName: string;
  address: any;
  industry: string;
  serialNumber: string;
  CSID_PrivateKey: string;
  CSID_CSR: string;
  CSID_RequestID: number;
  CSID_TokenType: string;
  CSID_BinarySecurityToken: string;
  CSID_Secret: string;
  CSID_DispositionMessage: string;
  commercialRegistrationNumber: string;
  SubmissionData_Supplier_StreetName: string;
  SubmissionData_Supplier_CityName: string;
  SubmissionData_Supplier_CitySubdivisionName: string;
  SubmissionData_Supplier_BuildingNumber: string;
  SubmissionData_Supplier_PostalZone: string;
}

export class ProductsSetting {
  nameUnique: boolean = false;
  skuRequired: boolean = true;
  skuUnique: boolean = true;
  partNumberRequired: boolean = true;
  partNumberUnique: boolean = true;
  brandRequired: boolean = false;
  brandUnique: boolean = false;

  static clone(value: any) {
    const settings = new ProductsSetting();
    Object.assign(settings, value);
    return settings;
  }
}

export enum InvoicingTransactions {
  All = 1,
  SalesInvoices = 2,
  PurchaseInvoices = 3
}

export enum InvoicingProductPrices {
  Inclusive = "Inclusive",
  Exclusive = "Exclusive"
}

export enum SubmissionMode {
  Instant = "Instant",
  Later = "Later"
}

export enum SalesRecognitionSetting {
  Fulfilled = 'fulfilled',
  Delivered = 'delivered'
}

export enum FulfillmentMode {
  Manual = 1,
  Automatic = 2
}

export enum AutomaticFulfillmentCondition {
  WhenOrderPaidAndDelivered = 1,
  WhenOrderDelivered = 2
}

export enum AvailableToSellFormula {
  OnHand_Refund_Orders_PreOrders = 'OnHand_Refund_Orders_PreOrders',
  OnHand_Refund_Orders = 'OnHand_Refund_Orders'
}

export enum ModuleVersion {
  V3_0 = 1,
  V2_5 = 2,
  UserLevel = 3
}